import { reactive } from 'vue'
import { deepCopy } from '@/util/deep-copy'
import i18n from '@/locale/i18n'

/*
 * 알럿 — 구 V2ReservationManagementDataController 의 마지막 조각 (MDP-2295 ④-5 7단계).
 *
 * 구조는 그대로 옮겼다. 전역 슬롯 하나(popup)에 type 을 세팅하면, 상시 마운트된 알럿 뷰
 * 여섯이 각자 자기 type 일 때만 자신을 그린다. 버튼을 누르면 함께 넣어 둔 콜백을 부른다.
 *
 * 이 구조의 한계는 알고 있다 — 알럿을 두 개 동시에 띄울 수 없고, type 문자열에 오타가
 * 나면 아무 일도 일어나지 않으며, 뷰 여섯이 거의 같은 템플릿을 여섯 벌 들고 있다.
 * Promise 기반 confirm 으로 바꾸면 정리되지만 알럿 뷰를 다시 짜야 해서 별도 작업으로 둔다.
 * ④ 의 목표는 컨트롤러 의존성 제거이고, 그건 이 이관으로 끝난다.
 */

type Handler = ((...args: never[]) => unknown) | undefined

/** 알럿 종류별 기본 생김새. titleKey/…Key 는 i18n 키이고 띄울 때 문구로 바뀐다. */
const ALERT_MODELS = {
    AlertEditUserInfo: {
        titleKey: 'alerts.edit_user_info.title',
        lButtonTitleKey: 'alerts.edit_user_info.left_button',
        rButtonTitleKey: 'alerts.edit_user_info.right_button',
        usingCloseButton: true,
        usingAlertTag: true,
    },
    AlertPhoneEdit: {
        boldString: '{{username}}',
        titleKey: 'alerts.phone_edit.title',
        lButtonTitleKey: 'alerts.phone_edit.left_button',
        rButtonTitleKey: 'alerts.phone_edit.right_button',
        usingCloseButton: false,
        usingAlertTag: false,
    },
    AlertSendPush: {
        lButtonTitleKey: 'alerts.send_push.left_button',
        rButtonTitleKey: 'alerts.send_push.right_button',
        usingCloseButton: false,
        usingAlertTag: true,
    },
    AlertRemoveConfirm: {
        titleKey: 'alerts.remove_confirm.title',
        lButtonTitleKey: 'alerts.remove_confirm.left_button',
        rButtonTitleKey: 'alerts.remove_confirm.right_button',
        usingCloseButton: false,
        usingAlertTag: true,
    },
    AlertGroupRemoveConfirm: {
        titleKey: 'alerts.group_remove_confirm.title',
        lButtonTitleKey: 'alerts.group_remove_confirm.left_button',
        rButtonTitleKey: 'alerts.group_remove_confirm.right_button',
        usingCloseButton: false,
        usingAlertTag: true,
    },
    AlertColumnRemoveConfirm: {
        titleKey: 'alerts.column_remove_confirm.title',
        lButtonTitleKey: 'alerts.column_remove_confirm.left_button',
        rButtonTitleKey: 'alerts.column_remove_confirm.right_button',
        usingCloseButton: false,
        usingAlertTag: true,
    },
} as const

export type AlertType = keyof typeof ALERT_MODELS

/** 알럿 뷰가 "내 차례인가" 를 가릴 때 쓴다 (구 Enum.PopupType) */
export const AlertPopupType = Object.fromEntries(
    Object.keys(ALERT_MODELS).map((k) => [k, k]),
) as Record<AlertType, AlertType>

type AlertPopup = Record<string, unknown> & {
    type?: string
    lHandler?: Handler
    rHandler?: Handler
}

const EMPTY: AlertPopup = { type: undefined, lHandler: undefined, rHandler: undefined }

export const sheetAlert = reactive({
    /** 지금 떠 있는 알럿. type 이 없으면 아무것도 떠 있지 않다 */
    popup: { ...EMPTY } as AlertPopup,
})

/**
 * i18n 키를 문구로 바꾸고, 넘어온 params 를 모델에 펼쳐 넣는다.
 * title 은 params.title 이 있으면 통째로 갈아 끼우고, params.userName 이 있으면 앞에 붙인다
 * (구 DataController.updatePopupModel 과 같은 순서 — 둘 다 있으면 userName 이 이긴다).
 */
function fillTexts(model: AlertPopup): AlertPopup {
    if (model.titleKey) {
        model.title = i18n.t(model.titleKey as string)
    }
    if (model.lButtonTitleKey) {
        model.lButtonTitle = i18n.t(model.lButtonTitleKey as string)
    }
    if (model.rButtonTitleKey) {
        model.rButtonTitle = i18n.t(model.rButtonTitleKey as string)
    }

    const params = model.params as Record<string, unknown> | undefined
    if (params != undefined) {
        for (const key of Object.keys(params)) {
            model[key] = params[key]
        }
        if (model.title && params.title) {
            model.title = params.title
        }
        if (model.title && params.userName) {
            model.title = String(params.userName) + String(model.title)
        }
    }

    return model
}

/**
 * 알럿을 띄운다.
 *
 * @param lHandler/rHandler 왼쪽·오른쪽 버튼을 눌렀을 때 부를 것
 * @param params            문구에 끼워 넣을 값 (userName 등)
 * @param lParam/rParam     각 핸들러에 그대로 넘길 값
 */
export function showAlertPopup(
    type: AlertType | string,
    lHandler?: Handler,
    rHandler?: Handler,
    params?: unknown,
    lParam?: unknown,
    rParam?: unknown,
): void {
    const model = (deepCopy(ALERT_MODELS[type as AlertType]) ?? {}) as AlertPopup
    model.type = type
    model.lHandler = lHandler
    model.rHandler = rHandler
    model.params = params
    model.lParam = lParam
    model.rParam = rParam
    sheetAlert.popup = fillTexts(model)
}

export function closeAlertPopup(): void {
    sheetAlert.popup = { ...EMPTY }
}
